import { format, getISOWeek, getYear } from 'date-fns';
import type { Send } from '../types/send';
import type { SendType, ClimbStyleType } from '../types/climb-log';

export type SendCategory = 'on_sight' | 'flash' | 'rotpunk';
export type TimePeriod = 'week' | 'month' | 'year';

export function getSendCategory(send_type: SendType | null | undefined): SendCategory {
  if (send_type === 'on_sight') return 'on_sight';
  if (send_type === 'flash') return 'flash';
  return 'rotpunk';
}

export function periodKey(date: Date, period: TimePeriod): string {
  if (period === 'year') return format(date, 'yyyy');
  if (period === 'month') return format(date, 'yyyy-MM');
  const week = String(getISOWeek(date)).padStart(2, '0');
  return `${getYear(date)}-W${week}`;
}

export function periodLabel(key: string, period: TimePeriod): string {
  if (period === 'year') return key;
  if (period === 'month') {
    const [year, month] = key.split('-');
    return format(new Date(Number(year), Number(month) - 1, 1), 'MMM yy');
  }
  // week: 2025-W18 → W18 '25
  const [yearPart, weekPart] = key.split('-');
  return `${weekPart} '${yearPart.slice(2)}`;
}

export function groupByPeriod(sends: Send[], period: TimePeriod): Record<string, Send[]> {
  const groups: Record<string, Send[]> = {};
  for (const send of sends) {
    if (!send.attributes.sent_at) continue;
    const key = periodKey(new Date(send.attributes.sent_at), period);
    if (!groups[key]) groups[key] = [];
    groups[key].push(send);
  }
  return groups;
}

export function sortedPeriodKeys(groups: Record<string, Send[]>): string[] {
  return Object.keys(groups).sort();
}

export function gradeName(difficultyValue: number, grades: Array<{ id: string; name: string; value: number }>): string {
  const sorted = [...grades].sort((a, b) => Math.abs(a.value - difficultyValue) - Math.abs(b.value - difficultyValue));
  return sorted[0]?.name ?? String(difficultyValue);
}

export const CHART_TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: 'hsl(var(--popover))',
    borderColor: 'hsl(var(--border))',
    color: 'hsl(var(--popover-foreground))',
    borderRadius: '6px',
    fontSize: '12px',
  },
  labelStyle: { color: 'hsl(var(--popover-foreground))' },
  itemStyle: { color: 'hsl(var(--popover-foreground))' },
} as const;

export const ALL_STYLES: ClimbStyleType[] = [
  'overhang', 'technical', 'strength', 'balance', 'coordination', 'endurance',
];

export const STYLE_COLORS: Record<ClimbStyleType, string> = {
  overhang: '#ef4444',
  technical: '#f97316',
  strength: '#eab308',
  balance: '#22c55e',
  coordination: '#3b82f6',
  endurance: '#a855f7',
};

export type StyleCounts = Record<ClimbStyleType, number>;

export function countByStyle(sends: Send[]): StyleCounts {
  const counts = Object.fromEntries(ALL_STYLES.map(s => [s, 0])) as StyleCounts;
  for (const send of sends) {
    for (const style of send.attributes.climb_style ?? []) {
      counts[style]++;
    }
  }
  return counts;
}
